/* global Config */

import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { useDropzone } from 'react-dropzone';
import { v4 as uuidv4 } from 'uuid';
import { isURL } from 'validator';

import Button from '../core/button';
import Error from '../core/error';
import './uploader.scss';

export default function Uploader({
                                   autoUpload = true,
                                   onChange,
                                 }) {
  const [file, setFile]           = useState(null);
  const [preview, setPreview]     = useState(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError]         = useState();
  const [uploadComplete, setUploadComplete] = useState(false);
  const [dimensions, setDimensions] = useState(null);

  // Clean up when a new file is selected or component unmounts
  useEffect(() => {
    return () => {
      if (preview) URL.revokeObjectURL(preview);
    };
  }, [preview]);

  const getAspectRatio = (w, h) => {
      const gcd = (a, b) => (b === 0 ? a : gcd(b, a % b));
      const divisor = gcd(w, h);
      return `${w / divisor}:${h / divisor}`;
  };

  // Perform the upload via fetch + FormData
  const uploadFile = async (f) => {
    setUploading(true);
    setError(undefined);

    const body = new FormData();
    body.append('uuid', uuidv4());
    body.append('filename', f.name);
    body.append('totalFileSize', f.size.toString());
    body.append('image', f);

    try {
      const res = await fetch(Config.base.imageUploadUrl, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'X-Requested-With': 'XMLHttpRequest',
          Accept: 'application/json',
          'Cache-Control': 'no-cache',
        },
        body,
      });

      const text = await res.text();
      if (!res.ok) {
        throw new Error(`Server returned ${res.status}`);
      }

      // your server returns the URL as plain text
      const uploadedUrl = text.trim();
      if (!isURL(uploadedUrl)) {
        throw new Error('Server did not return a valid URL');
      }

      onChange(uploadedUrl, f.name);
      setUploadComplete(true);
      const img = new Image();
      img.src = uploadedUrl;
      img.onload = () => {
        setDimensions({ width: img.width, height: img.height });
      };
    } catch (err) {
      setError(`Upload error: ${err.message}`);
    } finally {
      setUploading(false);
    }
  };

  // Handler for files dropped or selected
  const onDrop = useCallback(
    (accepted, rejected) => {
      if (rejected.length) {
        setError('Invalid file type or file too large.');
        return;
      }
      if (accepted.length) {
        const f = accepted[0];
        setFile(f);
        setPreview(URL.createObjectURL(f));
        setError(undefined);
        setUploadComplete(false);
        if (autoUpload) {
          uploadFile(f);
        }
      }
    },
    [autoUpload]
  );

  // Setup react-dropzone
  const {
    getRootProps,
    getInputProps,
    isDragActive,
    open,
  } = useDropzone({
    onDrop,
    accept: { 'image/jpeg': [], 'image/jpg': [] },
    multiple: false,
    maxSize: 5 * 1024 * 1024,
    onDropRejected: () => setError('Invalid file type or file too large.'),
    noClick: true,
    noKeyboard: true,
  });

  return (
      <div className="uploader">
          <div {...getRootProps({className: 'uploader--dropzone'})}>
              <input {...getInputProps()} />

              {/* placeholder when empty */}
              {!preview && <span className="uploader--dropzone-content">{isDragActive ? 'Drop file here…' : ''}</span>}

              {/* Action buttons */}
              <div className="uploader--actions">
                  <Button onClick={open} disabled={uploading}>
                      Choose File
                  </Button>
                  {file && !autoUpload && (
                      <Button onClick={() => uploadFile(file)} disabled={uploading}>
                          {uploading ? 'Uploading…' : 'Upload'}
                      </Button>
                  )}
              </div>

              {/* Preview or fallback */}
              {preview ? (
                  <div className="uploader--thumbnail-container">
                      <button
                          type="button"
                          className="uploader--remove-button"
                          onClick={() => {
                              setFile(null);
                              setPreview(null);
                              setError(undefined);
                          }}
                      >
                          ×
                      </button>
                      <img className="uploader--thumbnail" src={preview} alt="Preview" />
                      <div className="uploader--file-footer">
                          {dimensions && (
                              <div className="uploader--dimensions-block">
                                  <span className="uploader--dimensions">
                                      <span className="uploader-dimensions--width-height">{dimensions.width}×{dimensions.height}</span>
                                      <span className="uploader-dimensions--separator">&nbsp;</span>
                                      <span className="uploader-dimensions--aspect-ratio">({getAspectRatio(dimensions.width, dimensions.height)})</span>
                                  </span>
                              </div>
                          )}
                          <div><span className="uploader--filename">{file.name}</span></div>
                          {uploadComplete && (
                              <div>
                                  <span className="react-fine-uploader-status uploader--status"> Completed </span>
                                  <span className="react-fine-uploader-filesize uploader--filesize">
                                      <span className="react-fine-uploader-filesize-value">
                                          {(file.size / 1024).toFixed(2)}
                                      </span>
                                      <span className="react-fine-uploader-filesize-separator"> </span>
                                      <span className="react-fine-uploader-filesize-unit">KB</span>
                                  </span>
                              </div>
                          )}
                      </div>
                  </div>
              ) : null}
          </div>

          {error && <Error error={error} />}
      </div>
  );
}

Uploader.propTypes = {
    autoUpload: PropTypes.bool,
    onChange: PropTypes.func.isRequired,
};
